import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, ArrowRight } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { useCurrency } from "@/context/CurrencyContext";

export default function Pricing() {
  const [data, setData] = useState(null);
  const { currency } = useCurrency();
  const [billing, setBilling] = useState("monthly");
  const nav = useNavigate();

  useEffect(() => { api.get("/pricing-page", { params: { currency, annual: billing === "annual" } }).then(r => setData(r.data)).catch(() => {}); }, [currency, billing]);
  if (!data) return <div className="k-canvas" style={{ minHeight: "100vh", padding: 24 }}><span className="k-muted">Loading…</span></div>;

  const symbol = data.currency_symbol || "£";

  return (
    <div className="k-canvas" style={{ minHeight: "100vh" }} data-testid="pricing-page">
      <PublicNav />

      <section style={{ margin: "0 auto", padding: "56px 24px 20px" }}>
        <h1 className="k-heading" data-testid="pricing-headline" style={{ fontSize: "clamp(36px,5vw,56px)", letterSpacing: "-.03em", margin: 0 }}>{data.headline}</h1>
        <p className="k-muted" style={{ fontSize: 18, maxWidth: 680, marginTop: 14 }}>{data.subheadline}</p>

        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 22, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 4 }} data-testid="billing-switcher">
            {["monthly", "annual"].map(b => (
              <button key={b} className={`k-badge ${billing === b ? "k-badge-accent" : ""}`} style={{ cursor: "pointer" }}
                data-testid={`billing-${b}`} onClick={() => setBilling(b)}>{b}</button>
            ))}
          </div>
          <span className="k-muted" style={{ fontSize: 12 }} data-testid="fx-note">{data.fx_note}</span>
        </div>
      </section>

      {!data.pricing_is_public && (
        <section style={{ margin: "0 auto", padding: "0 24px 8px" }}>
          <div className="k-container" data-testid="pricing-waitlist" style={{ padding: 22, borderColor: "var(--border-strong)" }}>
            <div className="k-badge k-badge-accent" style={{ marginBottom: 8 }}>{data.illustrative_label}</div>
            <p className="k-muted" style={{ margin: 0 }}>{data.waitlist_note}</p>
            <button className="k-btn k-btn-primary" data-testid="request-early-access" style={{ marginTop: 12 }} onClick={() => nav("/register")}>Request early access <ArrowRight size={15} /></button>
          </div>
        </section>
      )}

      <section style={{ margin: "0 auto", padding: "20px 24px 30px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
        {data.tiers.map(t => (
          <div key={t.id} className="k-container k-accent-card" data-testid={`tier-${t.id}`} style={{ padding: 26 }}>
            <div className="k-heading" style={{ fontWeight: 700, fontSize: 20 }}>{t.name}</div>
            <div className="k-heading" style={{ fontSize: 32, margin: "10px 0 2px" }}>
              {t.state === "live" && t.display_price != null
                ? <>{symbol} {data.currency_display === "GBP" ? Number(t.display_price).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Math.ceil(t.display_price).toLocaleString("en-GB")}<span className="k-muted" style={{ fontSize: 14 }}>/{t.period}</span></>
                : <span className="k-muted" style={{ fontSize: 18 }} data-testid={`tier-${t.id}-waitlist`}>Coming soon</span>}
            </div>
            {t.state === "live" && t.display_price_gbp != null && data.currency_display !== "GBP" && (
              <div className="k-muted" data-testid={`tier-${t.id}-gbp`} style={{ fontSize: 12 }}>Billed as GBP {Number(t.display_price_gbp).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            )}
            {t.monthly_equiv_display != null && (
              <div className="k-muted" data-testid={`tier-${t.id}-annual-note`} style={{ fontSize: 12 }}>
                ≈ {symbol} {Math.ceil(t.monthly_equiv_display).toLocaleString("en-GB")}/mo · Save {data.annual_discount_pct}% — {symbol} {Math.ceil(t.annual_saving_display).toLocaleString("en-GB")}/year
              </div>
            )}
            {t.credits != null && <div className="k-muted" style={{ fontSize: 13, marginTop: 4 }}>{Number(t.credits).toLocaleString("en-GB")} credits{t.daily_cap ? ` · daily cap ${Number(t.daily_cap).toLocaleString("en-GB")}` : ""}</div>}
            <ul style={{ listStyle: "none", padding: 0, marginTop: 14 }}>
              {t.features.map((f, i) => (
                <li key={i} className="k-muted" style={{ fontSize: 13, display: "flex", gap: 8, marginBottom: 8, lineHeight: 1.4 }}>
                  <Check size={15} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} /> {f}
                </li>
              ))}
            </ul>
            <button className="k-btn k-btn-primary" data-testid={`tier-${t.id}-cta`} style={{ width: "100%", marginTop: 10 }} onClick={() => nav("/register")}>
              {t.id === "free" ? "Start free" : "Request early access"}
            </button>
          </div>
        ))}
      </section>

      <PublicFooter />
    </div>
  );
}
